// "This world is private — enter the access code."
//
// Shown by the viewer when someone opens a private world they aren't a member
// of, and by the dashboards' "Join private world" buttons (no worldId: the
// code alone finds the world). Joining adds the visitor to world_access, so
// they only ever need the code once.
import React, { useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { joinWorldWithCode, normalizeAccessCode } from '../services/worldJoin';
import './PrivateWorldGate.css';

interface Props {
  /** The world being opened; omit for a code-only join. */
  worldId?: string;
  worldName?: string;
  /** Prefill (e.g. from a `?code=` link); submitted automatically when signed in. */
  initialCode?: string;
  signedIn: boolean;
  /** Where "Sign in" should come back to. */
  returnTo?: string;
  onJoined: (worldId: string) => void;
  onCancel: () => void;
  cancelLabel?: string;
  /** 'page' fills its container (viewer); 'modal' floats over a page. */
  variant?: 'page' | 'modal';
}

const PrivateWorldGate: React.FC<Props> = ({
  worldId, worldName, initialCode = '', signedIn, returnTo, onJoined, onCancel,
  cancelLabel = 'Cancel', variant = 'modal',
}) => {
  const [code, setCode] = useState(initialCode.toUpperCase());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoTried = useRef(false);

  const submit = async (value = code) => {
    if (busy) return;
    if (!normalizeAccessCode(value)) { setError('Enter the world’s access code.'); return; }
    setBusy(true);
    setError(null);
    try {
      onJoined(await joinWorldWithCode(value, worldId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn’t check that code.');
      setBusy(false);
    }
  };

  useEffect(() => {
    if (signedIn && initialCode && !autoTried.current) {
      autoTried.current = true;
      void submit(initialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, initialCode]);

  const loginHref = `/login?next=${encodeURIComponent(returnTo || window.location.pathname + window.location.search)}`;

  return (
    <div className={`pwg-backdrop pwg-${variant}`} role="dialog" aria-modal="true" aria-labelledby="pwg-title">
      <div className="pwg-card">
        <div className="pwg-icon"><Lock size={22} /></div>
        <h2 id="pwg-title">{worldId ? 'This world is private' : 'Join a private world'}</h2>
        <p className="pwg-sub">
          {worldId
            ? <>{worldName ? <strong>{worldName}</strong> : 'This world'} needs an access code. Ask the owner for it — you&rsquo;ll only need it once.</>
            : 'Enter the access code the world’s owner gave you.'}
        </p>
        {signedIn ? (
          <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
            <input
              className="pwg-input"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); }}
              placeholder="ACCESS CODE"
              aria-label="Access code"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={16}
              autoFocus
            />
            {error && <p className="pwg-error" role="alert">{error}</p>}
            <div className="pwg-actions">
              <button type="submit" className="pwg-primary" disabled={busy}>{busy ? 'Checking…' : 'Enter world'}</button>
              <button type="button" className="pwg-ghost" onClick={onCancel}>{cancelLabel}</button>
            </div>
          </form>
        ) : (
          <div className="pwg-actions">
            <a className="pwg-primary" href={loginHref}>Sign in to enter the code</a>
            <button type="button" className="pwg-ghost" onClick={onCancel}>{cancelLabel}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivateWorldGate;
